import React from 'react';
import { Link } from 'react-router-dom';
import {
  User, Settings, Trophy, Dumbbell, Map, BookOpen, Award,
  ChevronRight, ArrowRight, MapPin, Calendar, Hash, Pen, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { KilometerCounter } from '../components/c2r/KilometerCounter';
import { StampBadge } from '../components/c2r/StampBadge';
import { EditorialKicker } from '../components/c2r/EditorialKicker';
import { PullQuote } from '../components/c2r/PullQuote';
import { SignPanel } from '../components/c2r/SignPanel';
import useSeo from '../hooks/useSeo';
import useGamification from '../hooks/useGamification';
import { cn } from '../lib/utils';

/**
 * Profil v2 — Carnet de bord du motard.
 *
 * Métaphore : un journal de bord en cuir cousu (moleskine), avec entrées
 * datées, cachets de validation, kilomètres parcourus, tampons d'étape.
 * Pas de XP/streak fitness. Du papier, de l'encre, du voyage.
 */

const CHAPTERS = [
  { id: 'first-session', title: 'Le premier départ', stamp: 'PREMIÈRE ÉTAPE', requirement: 'Termine ta 1ʳᵉ session', icon: Map, threshold: 1 },
  { id: 'streak-3', title: 'La régularité', stamp: 'CADENCE TENUE', requirement: '3 jours d\'affilée sur la route', icon: Calendar, threshold: 15 },
  { id: 'score-35', title: 'Le seuil franchi', stamp: 'SEUIL ATTEINT', requirement: 'Score >= 35/40 à l\'examen blanc', icon: Award, threshold: 35 },
  { id: 'chrono-killer', title: 'Le sang-froid', stamp: 'DÉCISION RAPIDE', requirement: 'Moyenne < 10s/question', icon: Hash, threshold: 50 },
  { id: 'lecture-laser', title: 'L\'œil du motard', stamp: 'LECTURE PRÉCISE', requirement: '95\u202f% de bonnes réponses sur 20', icon: BookOpen, threshold: 75 },
  { id: 'legende', title: 'La légende', stamp: 'LÉGENDE', requirement: 'Atteindre le niveau 5', icon: Trophy, threshold: 100 },
];

export default function Profil() {
  const g = useGamification();

  useSeo({
    title: 'Mon carnet de bord — Code 2 Roues',
    description: 'Ton carnet de bord vers le permis moto. Kilomètres parcourus, étapes validées, jalons.',
    path: '/profil',
  });

  // Métaphore : 1 XP = 1 km parcouru, 1 niveau = 1 étape
  const km = g.xp;
  const totalKm = 1500; // "voyage" complet jusqu'au permis
  const progressPct = Math.min(100, Math.round((km / totalKm) * 100));

  return (
    <div className="max-w-4xl mx-auto editorial-fade-up" data-testid="page-profil">
      {/* HEADER éditorial */}
      <header className="py-4 sm:py-6 border-b-2 border-foreground/15">
        <div className="flex items-center justify-between gap-3">
          <div>
            <EditorialKicker>Carnet de bord · Vol. 1</EditorialKicker>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.96] mt-1">
              Mon carnet<br />
              <span className="italic font-medium">de bord.</span>
            </h1>
          </div>
          <StampBadge label={g.levelName.toUpperCase()} date={`NIV.${g.level}`} className="hidden sm:inline-flex" />
        </div>
      </header>

      {/* Pull quote intro */}
      <PullQuote author="Texte de préface">
        Chaque session est un kilomètre de plus. Chaque erreur, une leçon retenue.
        Ce carnet retrace ton voyage vers le permis moto — page après page,
        étape après étape.
      </PullQuote>

      <hr className="editorial-rule my-6" />

      {/* Compteurs kilométriques */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="profil-counters">
        <KilometerCounter
          label="Distance parcourue"
          value={km.toLocaleString('fr-FR')}
          unit="km"
          hint={`sur ${totalKm.toLocaleString('fr-FR')} prévus`}
        />
        <KilometerCounter
          label="Carnet ouvert"
          value={g.streak}
          unit={g.streak > 1 ? 'jours' : 'jour'}
          hint={g.streakState === 'active' ? 'En cours' : g.streakState === 'at-risk' ? 'Bientôt inactif' : 'En pause'}
        />
        <KilometerCounter
          label="Étape"
          value={g.level}
          unit="/ 5"
          hint={g.isMax ? 'Voyage complet' : `Cap : ${g.nextLevelName}`}
        />
        <KilometerCounter
          label="Cachets"
          value={g.trophies.length}
          unit={`/ ${CHAPTERS.length}`}
          hint="Jalons tamponnés"
        />
      </section>

      <hr className="editorial-rule my-8" />

      {/* Progression du voyage */}
      <section className="editorial-card p-5 sm:p-7" data-testid="profil-progress">
        <EditorialKicker>Progression du voyage</EditorialKicker>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-1 mb-4">
          {progressPct} % du chemin parcouru
        </h2>

        <div className="relative h-3 rounded-full overflow-hidden bg-[hsl(var(--paper-aged))] border border-[hsl(var(--border))]">
          <div
            className="h-full bg-[hsl(var(--primary))] transition-all duration-1000"
            style={{ width: `${progressPct}%` }}
          />
          {/* Marques kilométriques */}
          <div className="absolute inset-0 flex justify-between items-center px-1 pointer-events-none">
            {[0, 25, 50, 75, 100].map((m) => (
              <span
                key={m}
                className="w-px h-2 bg-foreground/30"
                aria-hidden="true"
              />
            ))}
          </div>
        </div>
        <div className="flex justify-between text-[10px] mt-2 editorial-counter text-muted-foreground">
          <span>DÉPART</span>
          <span>1ʳᵉ étape</span>
          <span>MI-CHEMIN</span>
          <span>3ᵉ étape</span>
          <span>EXAMEN</span>
        </div>
      </section>

      <hr className="editorial-rule my-8" />

      {/* Chapitres / Cachets */}
      <section data-testid="profil-chapters">
        <div className="flex items-end justify-between gap-3 mb-5">
          <div>
            <EditorialKicker>Chapitres</EditorialKicker>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-1">
              Les jalons de ta route
            </h2>
          </div>
          <span className="editorial-counter text-sm font-bold text-muted-foreground">
            {g.trophies.length} / {CHAPTERS.length}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CHAPTERS.map((ch, i) => {
            const unlocked = g.trophies.some((ut) => ut.id === ch.id);
            const Icon = ch.icon;
            return (
              <article
                key={ch.id}
                data-testid={`chapter-${ch.id}`}
                data-unlocked={unlocked}
                className={cn(
                  'editorial-card editorial-card-hover p-4 sm:p-5 flex items-start gap-4',
                  !unlocked && 'opacity-50',
                )}
              >
                <div
                  className="editorial-step-number flex-shrink-0"
                  style={{ fontSize: '3rem', minWidth: 56, color: unlocked ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))' }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span className="editorial-kicker text-[9px]">{ch.stamp}</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-black tracking-tight leading-tight mt-1">
                    {ch.title}
                  </h3>
                  <p
                    className="text-sm text-muted-foreground italic leading-relaxed mt-1"
                    style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
                  >
                    {ch.requirement}
                  </p>
                  {unlocked && (
                    <div className="mt-2.5">
                      <StampBadge label="Tamponné" date={new Date().toLocaleDateString('fr-FR')} />
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <hr className="editorial-rule my-8" />

      {/* Actions rapides — style sommaire de magazine */}
      <section data-testid="profil-actions" className="space-y-2">
        <EditorialKicker>Sommaire</EditorialKicker>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-4">
          Où aller maintenant
        </h2>

        {[
          { to: '/revision', label: 'Repartir sur la route', subtitle: 'Une nouvelle session t\'attend', icon: Dumbbell, page: 'p. 04' },
          { to: '/mon-livret-moto', label: 'Mon livret d\'apprentissage', subtitle: '38 compétences officielles à valider (REMC)', icon: BookOpen, page: 'p. 06' },
          { to: '/parcours', label: 'Vue d\'ensemble du voyage', subtitle: 'Tous les chapitres et tes étapes', icon: Map, page: 'p. 08' },
          { to: '/magazine', label: 'Magazine hebdomadaire', subtitle: 'Conseils, articles, guides ECSR', icon: BookOpen, page: 'p. 12' },
          { to: '/mon-compte', label: 'Paramètres du carnet', subtitle: 'Notifications, profil, déconnexion', icon: Settings, page: 'p. 99' },
        ].map((row) => (
          <Link
            key={row.to}
            to={row.to}
            className="editorial-card editorial-card-hover p-4 flex items-center gap-4 group"
            data-testid={`profil-link-${row.to.slice(1) || 'home'}`}
          >
            <span className="editorial-counter text-xs text-muted-foreground w-12">{row.page}</span>
            <span className="w-px h-8 bg-border" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <div className="text-base font-black tracking-tight leading-tight">{row.label}</div>
              <div
                className="text-xs text-muted-foreground italic mt-0.5"
                style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
              >
                {row.subtitle}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
          </Link>
        ))}
      </section>

      <hr className="editorial-rule my-8" />

      {/* Encadré débug magazine "Ours" */}
      <aside
        className="editorial-card p-4 sm:p-5 bg-[hsl(var(--paper-aged))]"
        data-testid="profil-debug"
      >
        <EditorialKicker>L'ours — atelier</EditorialKicker>
        <p
          className="text-sm text-muted-foreground italic leading-relaxed mt-2 mb-3"
          style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
        >
          {g.isRemote
            ? `Ton carnet est sauvegardé sur ton compte. Ces boutons servent à tester ta progression — chaque action est synchronisée${g.isSynced ? '' : '…'}.`
            : 'Tu n’es pas connecté·e : ta progression est gardée sur cet appareil. Connecte-toi pour la retrouver partout.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { g.addXp(40); g.pingStreak(); }}
            className="editorial-btn-secondary"
            data-testid="profil-debug-add-xp"
          >
            <Pen className="w-3.5 h-3.5" />
            +40 km (session)
          </button>
          <button
            onClick={() => g.unlockTrophy(CHAPTERS[0])}
            className="editorial-btn-secondary"
            data-testid="profil-debug-unlock"
          >
            <Award className="w-3.5 h-3.5" />
            Tamponner un chapitre
          </button>
          <button
            onClick={() => g.reset()}
            className="editorial-btn-secondary opacity-60"
            data-testid="profil-debug-reset"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Réinitialiser
          </button>
        </div>
      </aside>
    </div>
  );
}
